import React, { useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useCRM } from '../context/CRMContext';

// Safely convert Firestore Timestamp or ISO string to JS Date
const getDate = (ts) => {
  if (!ts) return new Date(0);
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
};

const SOURCE_COLORS = {
  facebook: '#1877f2', instagram: '#e1306c', website: '#6366f1',
  whatsapp: '#25d366', referral: '#f59e0b', manual: '#94a3b8',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#f8fafc', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: '10px 14px' }}>
      <p style={{ color: '#475569', fontSize: 12, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{p.value}</span>
          <span style={{ fontSize: 11, color: '#64748b' }}>{p.name}</span>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({ label, value, sub, color, icon }) => (
  <div className="glass-card" style={{ padding: '20px 22px', borderTop: `2px solid ${color}` }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: 8, letterSpacing: '0.3px' }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{sub}</div>}
      </div>
      <div style={{ borderRadius: 10, padding: 10, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { leads, users, followups, pipelineStages } = useCRM();

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalLeads   = leads.length;
  const newToday     = leads.filter(l => getDate(l.createdAt) > new Date(Date.now() - 86400000)).length;
  const hotLeads     = leads.filter(l => l.priority === 'hot').length;

  // "Converted/Enrolled" = the lead's stage NAME indicates a real positive
  // outcome (enrolled / admitted / converted) — not "whichever stage happens
  // to sort last in the pipeline." That older heuristic miscounted leads
  // sitting in a terminal-but-negative stage (e.g. "Lost"/"Disqualified")
  // as conversions whenever that stage's `order` happened to be highest.
  // Matches the convention already used in Reports.js and DigitalMedia.js.
  const positiveStageIds = pipelineStages
    .filter(s => {
      const n = (s.name || '').toLowerCase();
      return n.includes('enroll') || n.includes('admit') || n.includes('convert');
    })
    .map(s => s.id);
  const converted    = leads.filter(l => positiveStageIds.includes(l.status)).length;
  const convRate     = totalLeads ? ((converted / totalLeads) * 100).toFixed(1) : 0;
  const overdueTasks = followups.filter(f => f.status === 'overdue').length;

  // ── Source breakdown ──────────────────────────────────────────────────────
  const sourceData = useMemo(() => {
    const map = leads.reduce((acc, l) => { acc[l.source || 'manual'] = (acc[l.source || 'manual'] || 0) + 1; return acc; }, {});
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [leads]);

  // ── Stage distribution ────────────────────────────────────────────────────
  const stageData = useMemo(() =>
    pipelineStages.map(s => ({
      name: s.name,
      count: leads.filter(l => l.status === s.id).length,
      color: s.color,
    })),
  [leads, pipelineStages]);

  // ── Counsellor performance ────────────────────────────────────────────────
  const counsellorData = useMemo(() =>
    users.map(u => {
      const safeName = (u.name || u.email || 'User').split(' ')[0];
      const safeBg   = u.avatar?.bg || '#7c3aed';
      return {
        name:      safeName,
        bg:        safeBg,
        leads:     leads.filter(l => l.assignedToId === u.id).length,
        converted: leads.filter(l => l.assignedToId === u.id && positiveStageIds.includes(l.status)).length,
      };
    }),
  [users, leads, positiveStageIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Weekly data (last 7 days) ─────────────────────────────────────────────
  const weeklyData = useMemo(() => {
    const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return Array.from({ length: 7 }, (_, i) => {
      const start = new Date(); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - (6 - i));
      const end   = new Date(start); end.setDate(end.getDate() + 1);
      return {
        day:      DAY_LABELS[start.getDay()],
        leads:    leads.filter(l => { const d = getDate(l.createdAt); return d >= start && d < end; }).length,
        followups: followups.filter(f => { const d = getDate(f.dueDate); return d >= start && d < end; }).length,
      };
    });
  }, [leads, followups]);

  // ── Monthly data (last 6 months) ──────────────────────────────────────────
  const monthlyData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const month = now.getMonth() - (5 - i);
      const year  = now.getFullYear() + Math.floor((now.getMonth() - (5 - i)) / 12);
      const start = new Date(year, ((month % 12) + 12) % 12, 1);
      const end   = new Date(year, ((month % 12) + 12) % 12 + 1, 1);
      return {
        month: start.toLocaleString('default', { month: 'short' }),
        leads: leads.filter(l => { const d = getDate(l.createdAt); return d >= start && d < end; }).length,
      };
    });
  }, [leads]);

  const prevMonthLeads = monthlyData[monthlyData.length - 2]?.leads || 0;
  const thisMonthLeads = monthlyData[monthlyData.length - 1]?.leads || 0;
  const growthPct = prevMonthLeads
    ? Math.round((thisMonthLeads - prevMonthLeads) / prevMonthLeads * 100)
    : thisMonthLeads > 0 ? 100 : 0;

  // ── Recent leads ──────────────────────────────────────────────────────────
  const recentLeads = useMemo(() =>
    [...leads].sort((a, b) => getDate(b.createdAt) - getDate(a.createdAt)).slice(0, 5),
  [leads]);

  return (
    <div className="page-enter" style={{ padding: '24px', overflowY: 'auto', height: '100%' }}>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="TOTAL LEADS" value={totalLeads} sub={`+${newToday} today`} color="#7c3aed"
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
        />
        <StatCard label="HOT LEADS" value={hotLeads} sub="High priority" color="#ef4444"
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>}
        />
        <StatCard label="CONV. RATE" value={`${convRate}%`} sub={`${converted} enrolled`} color="#10b981"
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>}
        />
        <StatCard label="OVERDUE TASKS" value={overdueTasks} sub="Need attention" color="#f59e0b"
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
        />
        <StatCard label="ACTIVE TEAM" value={users.length} sub="Counsellors" color="#3b82f6"
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
        />
      </div>

      {/* Charts row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16, marginBottom: 16 }}>
        {/* Weekly trend */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Lead Trend</div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>This week vs follow-ups</div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {[{ label: 'Leads', color: '#7c3aed' }, { label: 'Follow-ups', color: '#3b82f6' }].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: item.color }} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={weeklyData}>
              <defs>
                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorFollowups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis dataKey="day" tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="leads" stroke="#7c3aed" strokeWidth={2} fill="url(#colorLeads)" name="Leads" dot={{ fill: '#7c3aed', r: 3 }} />
              <Area type="monotone" dataKey="followups" stroke="#3b82f6" strokeWidth={2} fill="url(#colorFollowups)" name="Follow-ups" dot={{ fill: '#3b82f6', r: 3 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Source breakdown */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Lead Sources</div>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 16 }}>Where leads are coming from</div>
          {sourceData.length === 0 ? (
            <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
              No source data yet
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={sourceData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                    {sourceData.map((entry, i) => (
                      <Cell key={i} fill={SOURCE_COLORS[entry.name] || '#64748b'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ background: '#f8fafc', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 8 }}>
                {sourceData.map(s => (
                  <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: SOURCE_COLORS[s.name] || '#64748b', flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: '#475569', textTransform: 'capitalize' }}>{s.name}</span>
                    <span style={{ fontSize: 11, color: '#0f172a', fontWeight: 600 }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Charts row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Pipeline funnel */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Pipeline Distribution</div>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 16 }}>Leads by stage</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={stageData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} name="Leads">
                {stageData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Counsellor performance */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Team Performance</div>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 16 }}>Leads per counsellor</div>
          {counsellorData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#94a3b8', fontSize: 13 }}>
              No team members yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {counsellorData.sort((a, b) => b.leads - a.leads).map((c, i) => {
                const maxLeads = Math.max(...counsellorData.map(x => x.leads), 1);
                const pct = (c.leads / maxLeads) * 100;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="avatar" style={{ width: 26, height: 26, background: c.bg, color: '#fff', fontSize: 10 }}>
                          {c.name[0]}
                        </div>
                        <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{c.name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                        <span style={{ color: '#475569' }}>{c.leads} leads</span>
                        <span style={{ color: '#34d399', fontWeight: 600 }}>{c.converted} ✓</span>
                      </div>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${pct}%`, background: c.bg }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Monthly growth + Recent activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16 }}>
        {/* Monthly growth */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Monthly Growth</div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>Lead acquisition over time</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: growthPct >= 0 ? '#10b981' : '#ef4444' }}>
              {growthPct >= 0 ? '+' : ''}{growthPct}%
            </div>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="leads" radius={[6, 6, 0, 0]} name="Leads">
                {monthlyData.map((_, i) => (
                  <Cell key={i} fill={i === monthlyData.length - 1 ? '#7c3aed' : 'rgba(124,58,237,0.35)'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent activity */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Recent Leads</div>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 16 }}>Latest enquiries</div>
          {recentLeads.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, color: '#94a3b8', fontSize: 13 }}>
              No leads yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recentLeads.map(lead => {
                const src = lead.source || 'manual';
                const timeDiff = Math.round((Date.now() - getDate(lead.createdAt)) / 60000);
                const timeStr = timeDiff < 1 ? 'just now' : timeDiff < 60 ? `${timeDiff}m ago` : timeDiff < 1440 ? `${Math.round(timeDiff / 60)}h ago` : `${Math.round(timeDiff / 1440)}d ago`;
                return (
                  <div key={lead.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.04)' }}>
                    <div style={{ width: 34, height: 34, borderRadius: 9, background: `${SOURCE_COLORS[src] || '#94a3b8'}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                      {src === 'facebook' ? '📘' : src === 'instagram' ? '📸' : src === 'whatsapp' ? '💬' : src === 'website' ? '🌐' : src === 'referral' ? '🤝' : '✍️'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
                      <div style={{ fontSize: 11, color: '#475569' }}>{lead.country || '—'} · {lead.course || 'No course'}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 11, color: '#475569' }}>{timeStr}</div>
                      {lead.priority && (
                        <span className={`badge badge-${lead.priority}`} style={{ padding: '2px 7px', fontSize: 10 }}>{lead.priority}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
